import { Box, Tooltip, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useFieldEvidenceOptional } from './FieldEvidenceContext';

interface EvidenceNagLineProps {
  /**
   * Explicit count/labels for pages without a FieldEvidenceProvider (e.g. the
   * PLT batch page counts rows). Omit both to read from the page's provider.
   */
  count?: number;
  tooltip?: string;
  /** Noun for the count — default "field" ("3 fields missing evidence"). */
  noun?: string;
}

/**
 * The passive "evidence missing" nag — a one-line amber marker (never a
 * popup, never a save blocker). Renders nothing while loading, when the page
 * has no FieldEvidenceProvider (and no explicit count), or when all required
 * evidence is attached.
 */
export const EvidenceNagLine = ({ count, tooltip, noun = 'field' }: EvidenceNagLineProps) => {
  const theme = useTheme();
  const ctx = useFieldEvidenceOptional();

  const explicit = count !== undefined;
  const n = explicit ? count : (ctx && ctx.loaded ? ctx.missingRequired.length : 0);
  const tip = explicit
    ? (tooltip ?? '')
    : `Missing: ${(ctx?.missingRequired ?? []).map((f) => f.label).join(' · ')}`;
  if (n === 0) return null;

  return (
    <Tooltip title={tip}>
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75,
        px: 1.25, py: 0.4, borderRadius: 999,
        border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
        backgroundColor: alpha(theme.palette.warning.main, 0.08),
        cursor: 'default',
      }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: theme.palette.warning.main }} />
        <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 600, color: theme.palette.warning.dark }}>
          {n} {noun}{n > 1 ? 's' : ''} missing evidence
        </Typography>
      </Box>
    </Tooltip>
  );
};
